<script lang="ts" setup>
import { computed, ref, onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useTenantStore, useAuthStore } from '@/store';
import { DeleteTenantConfirm, RestoreTenantConfirm } from '../';
import AppTabMenu from '@/components/AppTabMenu';

const route = useRoute();
const id = ref<number>();
const tenantStore = useTenantStore();
const authStore = useAuthStore();
const openDeleteConfirm = ref<boolean>(false);
const openRestoreConfirm = ref<boolean>(false);
const router = useRouter();
const { tenant, selectedTenantRoutes } = storeToRefs(tenantStore);

onBeforeMount(async () => {
  if (route.params.id) {
    id.value = parseInt(route.params.id as string);
    await tenantStore.getTenant(id.value);
  }
});

const profileHeaderText = computed(() => (tenantStore.tenant ? tenantStore.tenant.name : ''));

const openConfirmDeleteTenant = () => (openDeleteConfirm.value = true);

const closeConfirmDeleteTenant = () => (openDeleteConfirm.value = false);

const openConfirmRestoreTenant = () => (openRestoreConfirm.value = true);

const closeConfirmRestoreTenant = () => (openRestoreConfirm.value = false);

const accessTenant = async () => {
  authStore.loading = true;
  const currentTenant = authStore.token?.tenant || undefined;
  let tenantToSwitch = authStore.token;

  if (currentTenant?.id !== tenant?.value?.id && id.value)
    tenantToSwitch = await tenantStore.getTokenToSwitchTenant(id.value);
  if (tenantToSwitch) await authStore.setTenantToSwitch(tenantToSwitch);
};

const redirectToMainPage = async () => await router.push('/admin');
</script>

<template>
  <div class="page-container">
    <DeleteTenantConfirm
      v-if="id !== authStore.masterTenantId"
      :id="id"
      :open="openDeleteConfirm"
      @update="redirectToMainPage"
      @cancel="closeConfirmDeleteTenant"
    />
    <RestoreTenantConfirm
      v-if="id !== authStore.masterTenantId"
      :id="id"
      :open="openRestoreConfirm"
      @update="redirectToMainPage"
      @cancel="closeConfirmRestoreTenant"
    />
    <div class="page-header w-full flex flex-wrap gap-3 align-items-center justify-content-between">
      <div>
        <RouterLink to="/admin" class="no-underline flex flex-row">
          <Icon name="arrow_back" class="text-xs" />
          <div class="link-margin text-xs">Back</div>
        </RouterLink>
        <Skeleton v-if="tenantStore.loading" width="20rem" height="2.3rem" />
        <h1 v-else class="pb-2">{{ profileHeaderText }}</h1>
        <AppTabMenu :items="selectedTenantRoutes" />
      </div>
      <div v-if="tenant" class="flex flex-wrap gap-3">
        <AppButton
          v-if="tenant?.deleted_at !== null"
          type="secondary"
          text="Restore"
          icon-left="delete-outline"
          @click="openConfirmRestoreTenant"
        />
        <div v-else class="flex gap-3">
          <AppButton
            v-if="id !== authStore.masterTenantId"
            type="secondary"
            text="Deactivate"
            icon-left="delete-outline"
            @click="openConfirmDeleteTenant"
          />
          <AppButton
            v-if="id !== authStore.masterTenantId"
            type="secondary"
            text="Access Tenant"
            icon-right="arrow_forward_outline"
            @click="accessTenant"
          />
        </div>
      </div>
    </div>
    <div class="page-content">
      <RouterView />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.router-link-active) {
  color: #3b4242;
}
</style>
